import { RAW_BS_DATA } from '../data/raw-data';
import { HOLIDAY_DATA } from '../data/holidays';

const START_BS_YEAR = 2000;
const END_BS_YEAR = 2089;
const NEPAL_OFFSET_MS = (5 * 60 + 45) * 60 * 1000; // +05:45 in milliseconds
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Anchor dates: 1 Baisakh 2000 BS = 14 April 1943 AD (at 00:00:00 Nepal Time)
const ANCHOR_BS_YEAR = 2000;
const ANCHOR_AD_YEAR = 1943;
const ANCHOR_AD_MONTH = 4; // April (1-based)
const ANCHOR_AD_DAY = 14;

// The absolute UTC millisecond timestamp when 1943-04-14 began in Nepal Time (+05:45)
// 1943-04-14 00:00:00 +05:45 corresponds to 1943-04-13 18:15:00 UTC
const ANCHOR_AD_NEPAL_MS = Date.UTC(ANCHOR_AD_YEAR, ANCHOR_AD_MONTH - 1, ANCHOR_AD_DAY) - NEPAL_OFFSET_MS;

const NEP_MONTHS: Record<string, number> = {
  'वैशाख': 1, 'बैशाख': 1,
  'जेठ': 2, 'ज्येष्ठ': 2,
  'असार': 3, 'आषाढ': 3,
  'साउन': 4, 'श्रावण': 4,
  'भदौ': 5, 'भाद्र': 5,
  'असोज': 6, 'आश्विन': 6,
  'कार्तिक': 7, 'कात्तिक': 7,
  'मंसिर': 8, 'मङ्सिर': 8, 'मार्ग': 8,
  'पुष': 9, 'पुस': 9, 'पौष': 9,
  'माघ': 10,
  'फागुन': 11, 'फाल्गुन': 11,
  'चैत': 12, 'चैत्र': 12
};

/**
 * Retrieves the number of days in a given BS year and month.
 * @param year BS year (2000 - 2089)
 * @param month BS month (1 - 12)
 */
export function getDaysInBSMonth(year: number, month: number): number {
  if (year < START_BS_YEAR || year > END_BS_YEAR) {
    throw new Error(`Year ${year} is out of supported BS range (${START_BS_YEAR}-${END_BS_YEAR})`);
  }
  if (month < 1 || month > 12) {
    throw new Error(`Month ${month} is invalid. Must be between 1 and 12.`);
  }
  // RAW_BS_DATA is typed as Record<string, number[]> since JSON keys are strings
  const yearData = RAW_BS_DATA[year.toString() as unknown as keyof typeof RAW_BS_DATA];
  if (!yearData) {
    throw new Error(`Data for BS year ${year} not found.`);
  }
  return yearData[month - 1];
}

/**
 * Checks if a given BS date is valid.
 * @param year BS year
 * @param month BS month (1-12)
 * @param day BS day
 */
export function isValidBSDate(year: number, month: number, day: number): boolean {
  try {
    const daysInMonth = getDaysInBSMonth(year, month);
    return day >= 1 && day <= daysInMonth;
  } catch {
    return false;
  }
}

/**
 * Helper to calculate the total number of days elapsed from BS 2000-01-01 to the given BS date.
 */
function getBsDaysElapsed(year: number, month: number, day: number): number {
  let elapsedDays = 0;

  // Add days for completed years
  for (let y = ANCHOR_BS_YEAR; y < year; y++) {
    const yearData = RAW_BS_DATA[y.toString() as unknown as keyof typeof RAW_BS_DATA];
    for (let m = 0; m < 12; m++) {
      elapsedDays += yearData[m];
    }
  }

  // Add days for completed months in the target year
  const currentYearData = RAW_BS_DATA[year.toString() as unknown as keyof typeof RAW_BS_DATA];
  for (let m = 0; m < month - 1; m++) {
    elapsedDays += currentYearData[m];
  }

  // Add days in the target month
  elapsedDays += (day - 1);

  return elapsedDays;
}

/**
 * Converts a Bikram Sambat (BS) date to a Gregorian (AD) Date object.
 * The returned Date object represents the start of the day (00:00:00) in the Nepal timezone (Asia/Kathmandu).
 */
export function bsToAd(year: number, month: number, day: number): Date;
export function bsToAd(dateStr: string): Date;
export function bsToAd(dateObj: { year: number; month: number; day: number }): Date;
export function bsToAd(arg1: any, arg2?: number, arg3?: number): Date {
  let year = 0;
  let month = 0;
  let day = 0;

  if (typeof arg1 === 'number' && typeof arg2 === 'number' && typeof arg3 === 'number') {
    year = arg1;
    month = arg2;
    day = arg3;
  } else if (typeof arg1 === 'string') {
    const parts = arg1.split(/[-/.]/);
    if (parts.length !== 3) {
      throw new Error(`Invalid BS date string format. Use YYYY-MM-DD.`);
    }
    year = parseInt(parts[0], 10);
    month = parseInt(parts[1], 10);
    day = parseInt(parts[2], 10);
  } else if (typeof arg1 === 'object' && arg1 !== null) {
    year = arg1.year;
    month = arg1.month;
    day = arg1.day;
  } else {
    throw new Error('Invalid arguments passed to bsToAd. Expected (year, month, day), a string, or an object.');
  }

  if (!isValidBSDate(year, month, day)) {
    throw new Error(`Invalid BS date: ${year}-${month}-${day}`);
  }

  const elapsedDays = getBsDaysElapsed(year, month, day);
  const targetMs = ANCHOR_AD_NEPAL_MS + elapsedDays * MS_PER_DAY;
  return new Date(targetMs);
}

/**
 * Converts a Gregorian (AD) date to a Bikram Sambat (BS) date object.
 * It uses the calendar date (Year, Month, Day) in the Nepal timezone (Asia/Kathmandu).
 */
export function adToBs(date: Date): { year: number; month: number; day: number; isHoliday: boolean; holidayName: string | null };
export function adToBs(dateStr: string): { year: number; month: number; day: number; isHoliday: boolean; holidayName: string | null };
export function adToBs(timestamp: number): { year: number; month: number; day: number; isHoliday: boolean; holidayName: string | null };
export function adToBs(arg1: any): { year: number; month: number; day: number; isHoliday: boolean; holidayName: string | null } {
  let adDate: Date;

  if (arg1 instanceof Date) {
    adDate = arg1;
  } else if (typeof arg1 === 'string') {
    adDate = new Date(arg1);
    if (isNaN(adDate.getTime())) {
      throw new Error(`Invalid AD date string: "${arg1}"`);
    }
  } else if (typeof arg1 === 'number') {
    adDate = new Date(arg1);
    if (isNaN(adDate.getTime())) {
      throw new Error(`Invalid AD timestamp: ${arg1}`);
    }
  } else {
    throw new Error('Invalid arguments passed to adToBs. Expected Date, string, or timestamp.');
  }

  // 1. Get the local calendar date of the AD Date in the Nepal timezone (+05:45).
  // We do this by offsetting the timestamp by 5h 45m and using UTC methods.
  const nepalTimeMs = adDate.getTime() + NEPAL_OFFSET_MS;
  const tempDate = new Date(nepalTimeMs);
  const adYear = tempDate.getUTCFullYear();
  const adMonth = tempDate.getUTCMonth() + 1; // 1-based
  const adDay = tempDate.getUTCDate();

  // 2. Calculate elapsed days between the anchor AD date and target AD date
  const targetUtcMs = Date.UTC(adYear, adMonth - 1, adDay);
  const anchorUtcMs = Date.UTC(ANCHOR_AD_YEAR, ANCHOR_AD_MONTH - 1, ANCHOR_AD_DAY);
  const diffMs = targetUtcMs - anchorUtcMs;
  let diffDays = Math.floor(diffMs / MS_PER_DAY);

  // If the target date is before our anchor date, throw out-of-range error
  if (diffDays < 0) {
    throw new Error(`Gregorian date ${adYear}-${adMonth}-${adDay} is before the supported range (starts BS ${START_BS_YEAR}-01-01 / AD 1943-04-14)`);
  }

  let bsYear = ANCHOR_BS_YEAR;
  let bsMonth = 1;
  let bsDay = 1;

  // 3. Determine the BS Year
  while (true) {
    if (bsYear > END_BS_YEAR) {
      throw new Error(`Gregorian date ${adYear}-${adMonth}-${adDay} is after the supported range (ends BS ${END_BS_YEAR}-12-30/31)`);
    }

    const yearData = RAW_BS_DATA[bsYear.toString() as unknown as keyof typeof RAW_BS_DATA];
    let yearTotalDays = 0;
    for (let m = 0; m < 12; m++) {
      yearTotalDays += yearData[m];
    }

    if (diffDays >= yearTotalDays) {
      diffDays -= yearTotalDays;
      bsYear++;
    } else {
      break;
    }
  }

  // 4. Determine the BS Month
  const currentYearData = RAW_BS_DATA[bsYear.toString() as unknown as keyof typeof RAW_BS_DATA];
  for (let m = 0; m < 12; m++) {
    const daysInMonth = currentYearData[m];
    if (diffDays >= daysInMonth) {
      diffDays -= daysInMonth;
      bsMonth++;
    } else {
      break;
    }
  }

  // 5. Remaining days represent the day of the month
  bsDay = diffDays + 1;

  // 6. Find holiday information
  let isHoliday = false;
  let holidayName: string | null = null;
  const holidaysForYear = HOLIDAY_DATA[bsYear.toString()];
  if (holidaysForYear) {
    const dateStr = `${bsYear}-${String(bsMonth).padStart(2, '0')}-${String(bsDay).padStart(2, '0')}`;
    const holidayEvent = holidaysForYear.find(h => h.date === dateStr);
    if (holidayEvent) {
      isHoliday = true;
      holidayName = holidayEvent.name;
    }
  }

  return { year: bsYear, month: bsMonth, day: bsDay, isHoliday, holidayName };
}
